import { useContext, useEffect, useState } from 'react'
import { Row, Col } from '@leroy-merlin-br/backyard-react'
import { Checkmark, HeartOutline } from '@backyard-ui/icons'

import { getPictures } from 'scripts/react-components/api/Product/pictures'
import * as dataLayer from 'scripts/utils/data-layer'

import WishlistProviderMethods from 'site/Wishlist/provider'
import { WishlistContext } from 'site/Wishlist/context'

import { CarouselItem, Video } from 'catalog/pages/NewProduct/types'

import {
  BuyBox,
  Disclaimer,
  Description,
  Badges,
  Carousel,
  Nav
} from './components'
import * as S from './styled'

interface ProductProps {
  isAssistedSale: boolean
  isWhatsappEnabled: boolean
  isOnDemand: boolean
  isOfferSchemaEnabled: boolean
  shouldRenderFreeShippingBadge: boolean
  shopName: string
  regionId: string
  description: string
  title: string
  productId: number
  url: string
  brandedInstallments: {
    amount: number
    value: string
  }
  installments: {
    amount: number
    value: string
  }
  discountPercentage: number
  discountType: string
  paymentMethodsDiscount: string
  price: {
    from?: {
      integers: string
      decimals: string
    }
    region?: {
      integers: string
      decimals: string
    }
    to: {
      integers: string
      decimals: string
    }
  }
  product: {
    brand: string
    hidePrice: boolean
    isSoldOut: boolean
    id: number
    img: string
    isFreeShipping: boolean
    isOutlet: boolean
    name: string
    unit: string
    hasCrossServices: boolean
  }
  packaging: {
    unit: string
    quantity: string
    label: string
    price: {
      integers: string
      decimals: string
    }
  }
  purchaseButtons: {
    ecommerce: boolean
    pickupInStore: boolean
    assistedSale: boolean
  }

  disclaimer: {
    isExclusiveOnEcommerce: boolean
    userRegion: string
    isAvailableOnEcommerce: boolean
    date: string
    region: string
  }

  badges: {
    cod: number
    tag?: {
      type: string
      label: string
    }
    ean?: string
    avs?: boolean
    expiredAvs?: boolean
    vef?: boolean
    bndes?: boolean
  }

  pictures: any
}

const Product = ({
  isAssistedSale,
  isWhatsappEnabled,
  isOnDemand,
  isOfferSchemaEnabled,
  shouldRenderFreeShippingBadge,
  shopName = 'Leroy Merlin',
  brandedInstallments,
  installments,
  discountPercentage,
  discountType,
  paymentMethodsDiscount,
  price,
  product,
  packaging,
  purchaseButtons,
  regionId,
  description,
  disclaimer,
  title,
  url,
  badges
}: ProductProps) => {
  const [carouselItems, setCarouselItems] = useState<CarouselItem[]>([])
  const [buttons, setButtons] = useState({
    hasVideos: false,
    hasSketchFab: false
  })

  const wishlistValues: any = useContext(WishlistContext)
  const isOnWishlist = wishlistValues.isOnWishlist(product.id)
  const { onPressHeart } = wishlistValues

  const wishlistAction = (event: Event) => {
    event.stopPropagation()
    dataLayer.midiasCarouselWishlistButtonClick(product.id)
    onPressHeart(
      {
        inWishList: isOnWishlist,
        id: product.id,
        product: { id: product.id, shortName: product.name, price }
      },
      'products'
    )
  }

  const wishListButton = {
    label: isOnWishlist ? 'Adicionado aos Favoritos' : 'Lista de Favoritos',
    icon: isOnWishlist ? Checkmark : HeartOutline,
    onClick: wishlistAction,
    variant: isOnWishlist ? 'solid' : 'ghost'
  }

  const videosWithThumbImages = (videos: Array<Video>) =>
    videos.map(
      (video: Video): Video => ({
        ...video,
        miniature: `https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg`,
        main: `https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg`
      })
    )

  useEffect(() => {
    getPictures(product.id).then(data => {
      const firstItem = data.images[0]
      const carouselItems = (firstItem ? [firstItem] : [])
        .concat(videosWithThumbImages(data.videos || []))
        .concat(data.sketchFab || [])
        .concat(data.images.slice(1))

      const carouselButtons = {
        hasVideos:
          carouselItems.filter((item: CarouselItem) => item.type === 'video').length > 0,
        hasSketchFab:
          carouselItems.filter((item: CarouselItem) => item.type === 'sketchFab')
            .length > 0
      }

      setCarouselItems(carouselItems)

      if (carouselButtons.hasVideos || carouselButtons.hasSketchFab) {
        setButtons(carouselButtons)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <WishlistProviderMethods>
      <S.Title size="giga" color="n900">
        {title}
      </S.Title>
      <Row>
        <Col size={{ mega: 8, giga: 7 }}>
          <S.CarouselAndBadges>
            <Badges
              productId={product.id}
              url={url}
              badges={badges}
              isAssistedSale={isAssistedSale}
            />

            <Carousel
              hasVideos={buttons.hasVideos}
              hasSketchFab={buttons.hasSketchFab}
              wishListButton={wishListButton}
              carouselItems={carouselItems}
              productId={product.id}
            />
          </S.CarouselAndBadges>
        </Col>

        <Col size={{ mega: 8, giga: 5 }}>
          <BuyBox
            isAssistedSale={isAssistedSale}
            isOnDemand={isOnDemand}
            isOfferSchemaEnabled={isOfferSchemaEnabled}
            shouldRenderFreeShippingBadge={shouldRenderFreeShippingBadge}
            shopName={shopName}
            brandedInstallments={brandedInstallments}
            installments={installments}
            discountPercentage={discountPercentage}
            discountType={discountType}
            paymentMethodsDiscount={paymentMethodsDiscount}
            price={price}
            product={product}
            packaging={packaging}
            regionId={regionId}
            purchaseButtons={purchaseButtons}
          />
          <Nav isWhatsappEnabled={isWhatsappEnabled} />
        </Col>
      </Row>

      <Row>
        <Col size={{ mega: 8, giga: 7 }}>
          <Disclaimer disclaimer={disclaimer} />
          <Description description={description} />
        </Col>
      </Row>
    </WishlistProviderMethods>
  )
}

export default Product
